/**
 * DrillProgressCard Component
 * 
 * Displays individual drill progress with completion status,
 * learning stage, and action buttons
 */

import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { StageBadge } from './StageBadge';
import type { DrillProgress } from '@/services/dashboard-progress';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle2, Circle, PlayCircle } from 'lucide-react';

interface DrillProgressCardProps {
  drill: DrillProgress;
}

export function DrillProgressCard({ drill }: DrillProgressCardProps) {
  const isComplete = drill.status === 'complete' || drill.status === 'mastered';
  const isInProgress = drill.status === 'in_progress';
  const isNotStarted = drill.status === 'not_started';

  const StatusIcon = isComplete ? CheckCircle2 : isInProgress ? PlayCircle : Circle;

  const formattedDate = drill.last_attempt_date
    ? formatDistanceToNow(new Date(drill.last_attempt_date), { addSuffix: true })
    : 'Never attempted';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        {/* Header with status icon */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-start gap-2 flex-1">
            <StatusIcon
              className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                isComplete
                  ? 'text-green-600'
                  : isInProgress
                  ? 'text-blue-600'
                  : 'text-gray-400'
              }`}
              aria-label={
                isComplete
                  ? 'Completed'
                  : isInProgress
                  ? 'In progress'
                  : 'Not started'
              }
            />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">
                {drill.title}
              </h3>
            </div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-600">
              {drill.sections_completed}/{drill.total_sections} sections
            </span>
            <span className="text-xs font-medium text-gray-900">
              {drill.completion_percentage}%
            </span>
          </div>
          <Progress 
            value={drill.completion_percentage} 
            className="h-2"
            aria-label={`${drill.completion_percentage}% complete`}
          />
        </div>

        {/* Stage badge and last attempt */}
        <div className="flex items-center justify-between mb-3">
          <StageBadge stage={drill.learning_stage} />
          <span className="text-xs text-gray-500">
            {formattedDate}
          </span>
        </div>

        {/* Action button */}
        <Link to={`/drills/${drill.drill_id}`}>
          <Button
            variant={isInProgress ? 'default' : 'outline'}
            size="sm"
            className="w-full"
          >
            {isNotStarted && 'Start Drill'}
            {isInProgress && 'Resume'}
            {isComplete && 'Review'}
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

